import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Clock,
  Phone,
  User,
  Truck,
  FileText,
  Edit,
  Check,
  X,
  RotateCcw,
  Trash2,
  Download,
  CalendarPlus,
  Image,
  Building,
  Route,
  Fuel,
  Printer,
  ExternalLink,
  Camera,
  Receipt
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ServiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [clients, setClients] = useState([]);
  const [additionalServices, setAdditionalServices] = useState([]);
  
  // Dialog states
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showPhotosDialog, setShowPhotosDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  
  // Form states
  const [editForm, setEditForm] = useState({});
  const [completeForm, setCompleteForm] = useState({
    fuel: '',
    tolls: '',
    parking: '',
    taxi: '',
    other: '',
    other_description: '',
    completion_notes: ''
  });
  
  // Photos states
  const [pickupPhotos, setPickupPhotos] = useState([]);
  const [deliveryPhotos, setDeliveryPhotos] = useState([]);
  const [expensePhotos, setExpensePhotos] = useState([]);
  const [contractPhotos, setContractPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [vehiclePhotos, setVehiclePhotos] = useState(null);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  
  // Driver search
  const [driverSearchTerm, setDriverSearchTerm] = useState('');
  const [showDriverOptions, setShowDriverOptions] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadServiceData();
  }, [id, navigate]);

  const loadServiceData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Load order, drivers, clients, and additional services in parallel
      const [orderRes, driversRes, clientsRes, servicesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/orders/${id}`, { headers }),
        fetch(`${BACKEND_URL}/api/drivers`, { headers }),
        fetch(`${BACKEND_URL}/api/users`, { headers }),
        fetch(`${BACKEND_URL}/api/additional-services`, { headers })
      ]);

      if (orderRes.ok) {
        const orderData = await orderRes.json();
        setOrder(orderData);
        initializeEditForm(orderData);
      } else {
        toast.error(t('service_toast_load_failed'));
        navigate('/admin/dashboard');
      }

      if (driversRes.ok) {
        const driversData = await driversRes.json();
        setDrivers(driversData);
      }

      if (clientsRes.ok) {
        const clientsData = await clientsRes.json();
        setClients(clientsData.filter(u => u.role === 'client'));
      }

      if (servicesRes.ok) {
        const servicesData = await servicesRes.json();
        setAdditionalServices(servicesData);
      }
    } catch (error) {
      console.error('Error loading service:', error);
      toast.error(t('service_toast_error_load'));
    } finally {
      setLoading(false);
    }
  };

  const initializeEditForm = (orderData) => {
    setEditForm({
      status: orderData.status || 'pending',
      driver_id: orderData.driver_id || '',
      car_plate: orderData.car_plate || '',
      car_brand: orderData.car_brand || '',
      car_model: orderData.car_model || '',
      pickup_address: orderData.pickup_address || '',
      delivery_address: orderData.delivery_address || '',
      pickup_contact_name: orderData.pickup_contact_name || '',
      pickup_contact_phone: orderData.pickup_contact_phone || '',
      delivery_contact_name: orderData.delivery_contact_name || '',
      delivery_contact_phone: orderData.delivery_contact_phone || '',
      pickup_date_from: orderData.pickup_date_from || '',
      pickup_time_start: orderData.pickup_time_start || '',
      pickup_time_end: orderData.pickup_time_end || '',
      delivery_date_from: orderData.delivery_date_from || '',
      delivery_time_start: orderData.delivery_time_start || '',
      delivery_time_end: orderData.delivery_time_end || '',
      distance_km: orderData.distance_km || 0,
      total_price: orderData.total_price || 0,
      driver_price: orderData.driver_price || 0,
      notes: orderData.notes || ''
    });
    
    const assignedDriver = drivers.find(d => d.id === orderData.driver_id);
    setDriverSearchTerm(assignedDriver ? assignedDriver.name : '');
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.company_name || client?.name || t('service_no_client');
  };

  const getDriverName = (driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    return driver?.name || t('service_no_driver');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return t('service_status_undefined');
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return t('service_status_undefined');
      return date.toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB', { 
        weekday: 'short', 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return t('service_status_undefined');
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5);
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { bg: 'bg-yellow-500', text: t('service_status_pending') },
      assigned: { bg: 'bg-blue-500', text: t('service_status_assigned') },
      in_progress: { bg: 'bg-purple-500', text: t('service_status_in_progress') },
      in_transit: { bg: 'bg-indigo-500', text: t('service_status_in_transit') },
      delivered: { bg: 'bg-green-500', text: t('service_status_delivered') },
      cancelled: { bg: 'bg-red-500', text: t('service_status_cancelled') }
    };
    return configs[status] || { bg: 'bg-gray-500', text: status };
  };

  // Action handlers
  const handleUpdateOrder = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        toast.success(t('service_toast_updated'));
        setShowEditDialog(false);
        loadServiceData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('service_toast_error_update'));
      }
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error(t('service_toast_error_update_service'));
    }
  };

  const handleUploadPhoto = async (event, category) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    setUploadingPhoto(true);
    try {
      const token = localStorage.getItem('admin_token');
      const uploadedUrls = [];

      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('photo_category', category);

        const response = await fetch(`${BACKEND_URL}/api/admin/orders/${id}/upload-completion-photo`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (response.ok) {
          const data = await response.json();
          uploadedUrls.push(`/api/photos/${data.photo_id}`);
        } else {
          toast.error(t('service_toast_error_upload_file', { fileName: file.name }));
        }
      }

      if (uploadedUrls.length > 0) {
        if (category === 'pickup') setPickupPhotos(prev => [...prev, ...uploadedUrls]);
        else if (category === 'delivery') setDeliveryPhotos(prev => [...prev, ...uploadedUrls]);
        else if (category === 'expense') setExpensePhotos(prev => [...prev, ...uploadedUrls]);
        else if (category === 'contract') setContractPhotos(prev => [...prev, ...uploadedUrls]);
        toast.success(t('service_toast_photos_uploaded', { count: uploadedUrls.length }));
      }
    } catch (error) {
      console.error('Error uploading photos:', error);
      toast.error(t('service_toast_error_upload_photos'));
    } finally {
      setUploadingPhoto(false);
      event.target.value = '';
    }
  };

  const handleCompleteService = async () => {
    const extractPhotoId = (url) => url.split('/').pop();

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${id}/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          expenses: {
            fuel: parseFloat(completeForm.fuel) || 0,
            tolls: parseFloat(completeForm.tolls) || 0,
            parking: parseFloat(completeForm.parking) || 0,
            taxi: parseFloat(completeForm.taxi) || 0,
            other: parseFloat(completeForm.other) || 0,
            other_description: completeForm.other_description
          },
          pickup_photos: pickupPhotos.map(extractPhotoId),
          delivery_photos: deliveryPhotos.map(extractPhotoId),
          expense_photos: expensePhotos.map(extractPhotoId),
          contract_photos: contractPhotos.map(extractPhotoId),
          completion_notes: completeForm.completion_notes
        })
      });

      if (response.ok) {
        toast.success(t('service_toast_completed'));
        setShowCompleteDialog(false);
        loadServiceData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('service_toast_error_complete'));
      }
    } catch (error) {
      console.error('Error completing service:', error);
      toast.error(t('service_toast_error_complete_service'));
    }
  };

  const handleRevertToPending = async () => {
    if (!window.confirm(t('service_confirm_revert'))) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${id}/revert-to-pending`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success(t('service_toast_reverted'));
        loadServiceData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('service_toast_error_revert'));
      }
    } catch (error) {
      console.error('Error reverting:', error);
      toast.error(t('service_toast_error_revert_service'));
    }
  };

  const handleDeleteOrder = async () => {
    if (!window.confirm(t('service_confirm_delete'))) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(t('service_toast_deleted'));
        navigate('/admin/dashboard');
      } else {
        const error = await response.json();
        toast.error(error.detail || t('service_toast_error_delete'));
      }
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(t('service_toast_error_delete_service'));
    }
  };

  const handleCancelOrder = async (cancelType = 'normal') => {
    try {
      const token = localStorage.getItem('admin_token');
      let endpoint = `${BACKEND_URL}/api/admin/orders/${id}/cancel`;
      let body = {};

      if (cancelType === 'pickup') {
        endpoint = `${BACKEND_URL}/api/admin/orders/${id}/cancel-at-pickup`;
      } else if (cancelType === 'transit') {
        endpoint = `${BACKEND_URL}/api/admin/orders/${id}/cancel-in-transit`;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        toast.success(t('service_toast_cancelled'));
        setShowCancelDialog(false);
        loadServiceData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('service_toast_error_cancel'));
      }
    } catch (error) {
      console.error('Error canceling:', error);
      toast.error(t('service_toast_error_cancel_service'));
    }
  };

  const handleDownloadContract = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${id}/generate-contract?token=${token}`);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Contrato_AutoGo_${id.substring(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(t('service_toast_contract_downloaded'));
      } else {
        toast.error(t('service_toast_error_generate_contract'));
      }
    } catch (error) {
      console.error('Error downloading contract:', error);
      toast.error(t('service_toast_error_download_contract'));
    }
  };

  const handleAddToCalendar = () => {
    if (!order) return;
    
    const driver = drivers.find(d => d.id === order.driver_id);
    const driverName = driver ? driver.name : t('service_no_driver');
    
    const pickupCity = order.pickup_address ? order.pickup_address.split(',').pop().trim() : 'N/A';
    const deliveryCity = order.delivery_address ? order.delivery_address.split(',').pop().trim() : 'N/A';
    
    const title = `${order.car_plate || t('no_plate')} - ${driverName} - ${pickupCity} → ${deliveryCity}`;
    
    let pickupDate = order.pickup_date_from ? new Date(order.pickup_date_from) : new Date();
    if (order.pickup_time_start) {
      const [hours, minutes] = order.pickup_time_start.split(':');
      pickupDate.setHours(parseInt(hours) || 9, parseInt(minutes) || 0, 0, 0);
    }
    
    let deliveryDate = order.delivery_date_to ? new Date(order.delivery_date_to) : new Date(pickupDate.getTime() + 2 * 60 * 60 * 1000);
    if (order.delivery_time_end) {
      const [hours, minutes] = order.delivery_time_end.split(':');
      deliveryDate.setHours(parseInt(hours) || 18, parseInt(minutes) || 0, 0, 0);
    }
    
    const formatDateForGoogleCal = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}${month}${day}T${hours}${minutes}00`;
    };
    
    const details = `Servicio AutoGo
Vehículo: ${order.car_brand} ${order.car_model} (${order.car_plate})
Conductor: ${driverName}

📍 Recogida: ${order.pickup_address}
📍 Entrega: ${order.delivery_address}`;
    
    const baseUrl = 'https://calendar.google.com/calendar/render';
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: title,
      dates: `${formatDateForGoogleCal(pickupDate)}/${formatDateForGoogleCal(deliveryDate)}`,
      details: details,
      location: `${order.pickup_address} → ${order.delivery_address}`,
      trp: 'false'
    });
    
    window.open(`${baseUrl}?${params.toString()}`, '_blank');
    toast.success(t('service_toast_opening_calendar'));
  };

  const loadVehiclePhotos = async () => {
    setLoadingPhotos(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${id}/vehicle-photos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setVehiclePhotos(data);
      }
    } catch (error) {
      console.error('Error loading photos:', error);
    } finally {
      setLoadingPhotos(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#36e961] mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando servicio...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">{t('service_not_found')}</p>
          <Button onClick={() => navigate('/admin/dashboard')} className="mt-4">
            {t('back_to_dashboard')}
          </Button>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(order.status);
  const isCancelled = order.status?.includes('Cancelado') || order.status === 'cancelled';
  const isDelivered = order.status === 'delivered';

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-[#1a1f2e] text-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin/dashboard')}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>
              <div className="flex items-center gap-3">
                <div className="bg-[#36e961] p-2 rounded-lg">
                  <Truck className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold">Servicio #{id.substring(0, 8).toUpperCase()}</h1>
                  <p className="text-sm text-gray-300">{getClientName(order.client_id)}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`${statusConfig.bg} px-4 py-1.5 rounded-full text-sm font-medium`}>
                {statusConfig.text}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                className="border-white/30 text-white hover:bg-white/10"
              >
                <Printer className="w-4 h-4 mr-2" />
                Imprimir
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Summary Bar */}
        <Card>
          <CardContent className="py-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-sm text-gray-500">Precio Cliente</p>
                <p className="text-2xl font-bold text-green-600">€{order.total_price?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500">Precio Conductor</p>
                <p className="text-2xl font-bold text-blue-600">€{order.driver_price?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500">{t('driver_billing_driver')}</p>
                <p className="text-lg font-semibold flex items-center justify-center gap-2">
                  <User className="w-4 h-4" />
                  {order.driver_id ? getDriverName(order.driver_id) : <span className="text-orange-500">{t('no_driver_assigned')}</span>}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500">{t('distance')}</p>
                <p className="text-lg font-semibold flex items-center justify-center gap-2">
                  <Route className="w-4 h-4" />
                  {order.distance_km ? `${order.distance_km} km` : t('service_status_not_calculated')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pickup and Delivery */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Pickup Card */}
          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-green-700">
                <div className="bg-green-100 p-2 rounded-full">
                  <MapPin className="w-5 h-5 text-green-600" />
                </div>
                RECOGIDA
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="font-medium text-gray-900">{order.pickup_address || t('address_not_specified')}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(order.pickup_date_from)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>
                    {formatTime(order.pickup_time_start) || '—'} 
                    {order.pickup_time_end && ` - ${formatTime(order.pickup_time_end)}`}
                  </span>
                </div>
              </div>
              {(order.pickup_contact_name || order.pickup_contact_phone) && (
                <div className="pt-2 border-t">
                  <p className="text-sm text-gray-500">Contacto:</p>
                  <div className="flex items-center gap-4 mt-1">
                    {order.pickup_contact_name && (
                      <span className="flex items-center gap-1 text-sm">
                        <User className="w-3 h-3" />
                        {order.pickup_contact_name}
                      </span>
                    )}
                    {order.pickup_contact_phone && (
                      <a href={`tel:${order.pickup_contact_phone}`} className="flex items-center gap-1 text-sm text-blue-600 hover:underline">
                        <Phone className="w-3 h-3" />
                        {order.pickup_contact_phone}
                      </a>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Delivery Card */}
          <Card className="border-l-4 border-l-red-500">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-red-700">
                <div className="bg-red-100 p-2 rounded-full">
                  <MapPin className="w-5 h-5 text-red-600" />
                </div>
                ENTREGA
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="font-medium text-gray-900">{order.delivery_address || t('address_not_specified')}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(order.delivery_date_from)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>
                    {formatTime(order.delivery_time_start) || '—'} 
                    {order.delivery_time_end && ` - ${formatTime(order.delivery_time_end)}`}
                  </span>
                </div>
              </div>
              {(order.delivery_contact_name || order.delivery_contact_phone) && (
                <div className="pt-2 border-t">
                  <p className="text-sm text-gray-500">Contacto:</p>
                  <div className="flex items-center gap-4 mt-1">
                    {order.delivery_contact_name && (
                      <span className="flex items-center gap-1 text-sm">
                        <User className="w-3 h-3" />
                        {order.delivery_contact_name}
                      </span>
                    )}
                    {order.delivery_contact_phone && (
                      <a href={`tel:${order.delivery_contact_phone}`} className="flex items-center gap-1 text-sm text-blue-600 hover:underline">
                        <Phone className="w-3 h-3" />
                        {order.delivery_contact_phone}
                      </a>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Vehicle Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              VEHÍCULO
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <Truck className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p className="text-xs text-gray-500">Tipo</p>
                <p className="font-medium">{order.car_type || t('car_type_turismo')}</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Marca</p>
                <p className="font-medium text-lg">{order.car_brand || '—'}</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Modelo</p>
                <p className="font-medium text-lg">{order.car_model || '—'}</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Matrícula</p>
                <p className="font-bold text-lg font-mono">{order.car_plate || '—'}</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <Fuel className="w-5 h-5 mx-auto mb-1 text-gray-400" />
                <p className="text-xs text-gray-500">Combustible</p>
                <p className="font-medium">{order.fuel_type || 'Gasolina'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Documents & Photos */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Documents */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                DOCUMENTOS
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {order.driver_id ? (
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={handleDownloadContract}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar Contrato de Transporte
                </Button>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  Asigna un conductor para generar el contrato
                </p>
              )}
            </CardContent>
          </Card>

          {/* Photos */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5" />
                FOTOS
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  loadVehiclePhotos();
                  setShowPhotosDialog(true);
                }}
              >
                <Image className="w-4 h-4 mr-2" />
                Ver Fotos del Servicio
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Notes - aligned with admin/driver/client */}
        {(order.notes || order.first_vehicle_notes || order.second_vehicle_notes) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">📝 {t('admin_service_notes')}</CardTitle>
            </CardHeader>
            <CardContent className="bg-amber-50/50 border border-amber-200 rounded-lg space-y-3">
              {order.notes && (
                <div>
                  <p className="text-xs font-medium text-amber-700">{t('admin_notes_general')}</p>
                  <p className="text-gray-700 whitespace-pre-wrap mt-0.5">{order.notes}</p>
                </div>
              )}
              {order.first_vehicle_notes && (
                <div>
                  <p className="text-xs font-medium text-amber-700">{t('admin_notes_vehicle1')}</p>
                  <p className="text-gray-700 whitespace-pre-wrap mt-0.5">{order.first_vehicle_notes}</p>
                </div>
              )}
              {order.second_vehicle_notes && (
                <div>
                  <p className="text-xs font-medium text-amber-700">{t('admin_notes_vehicle2')}</p>
                  <p className="text-gray-700 whitespace-pre-wrap mt-0.5">{order.second_vehicle_notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <Card>
          <CardHeader>
            <CardTitle>Acciones</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {/* Edit */}
              <Button
                variant="outline"
                onClick={() => {
                  initializeEditForm(order);
                  setShowEditDialog(true);
                }}
              >
                <Edit className="w-4 h-4 mr-2" />
                Editar Servicio
              </Button>

              {/* Complete - only for non-delivered, non-cancelled */}
              {!isCancelled && !isDelivered && (
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => setShowCompleteDialog(true)}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Completar Servicio
                </Button>
              )}

              {/* Revert - only for delivered */}
              {isDelivered && (
                <Button
                  variant="outline"
                  className="border-orange-400 text-orange-700"
                  onClick={handleRevertToPending}
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Revertir a En Proceso
                </Button>
              )}

              {/* Calendar */}
              <Button
                variant="outline"
                onClick={handleAddToCalendar}
              >
                <CalendarPlus className="w-4 h-4 mr-2" />
                Añadir a Calendar
              </Button>

              {/* Cancel - only for non-cancelled, non-delivered */}
              {!isCancelled && !isDelivered && (
                <Button
                  variant="outline"
                  className="border-red-300 text-red-700"
                  onClick={() => setShowCancelDialog(true)}
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancelar Servicio
                </Button>
              )}

              {/* Delete */}
              <Button
                variant="destructive"
                onClick={handleDeleteOrder}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Servicio</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label>Estado</Label>
              <Select
                value={editForm.status}
                onValueChange={(value) => setEditForm({ ...editForm, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pendiente</SelectItem>
                  <SelectItem value="assigned">Asignado</SelectItem>
                  <SelectItem value="in_transit">En Tránsito</SelectItem>
                  <SelectItem value="delivered">Entregado</SelectItem>
                  <SelectItem value="cancelled">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Conductor</Label>
              <Select
                value={editForm.driver_id || 'no-driver'}
                onValueChange={(value) => setEditForm({ ...editForm, driver_id: value === 'no-driver' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('placeholder_select_driver')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no-driver">Sin asignar</SelectItem>
                  {drivers.map((driver) => (
                    <SelectItem key={driver.id} value={driver.id}>
                      {driver.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Marca</Label>
              <Input
                value={editForm.car_brand}
                onChange={(e) => setEditForm({ ...editForm, car_brand: e.target.value })}
              />
            </div>
            <div>
              <Label>Modelo</Label>
              <Input
                value={editForm.car_model}
                onChange={(e) => setEditForm({ ...editForm, car_model: e.target.value })}
              />
            </div>
            <div>
              <Label>Matrícula</Label>
              <Input
                value={editForm.car_plate}
                onChange={(e) => setEditForm({ ...editForm, car_plate: e.target.value })}
              />
            </div>
            <div>
              <Label>Distancia (km)</Label>
              <Input
                type="number"
                value={editForm.distance_km}
                onChange={(e) => setEditForm({ ...editForm, distance_km: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Precio Cliente (€)</Label>
              <Input
                type="number"
                value={editForm.total_price}
                onChange={(e) => setEditForm({ ...editForm, total_price: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Precio Conductor (€)</Label>
              <Input
                type="number"
                value={editForm.driver_price}
                onChange={(e) => setEditForm({ ...editForm, driver_price: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="col-span-2">
              <Label>Dirección Recogida</Label>
              <Input
                value={editForm.pickup_address}
                onChange={(e) => setEditForm({ ...editForm, pickup_address: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label>Dirección Entrega</Label>
              <Input
                value={editForm.delivery_address}
                onChange={(e) => setEditForm({ ...editForm, delivery_address: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label>Notas</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateOrder} className="bg-[#36e961] hover:bg-[#2dd351]">
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Service Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Completar Servicio</DialogTitle>
            <DialogDescription>
              Añade los gastos y fotos para completar el servicio
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Expenses */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Receipt className="w-4 h-4" />
                Gastos
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <Label>Combustible (€)</Label>
                  <Input
                    type="number"
                    value={completeForm.fuel}
                    onChange={(e) => setCompleteForm({ ...completeForm, fuel: e.target.value })}
                    placeholder={t('placeholder_amount')}
                  />
                </div>
                <div>
                  <Label>Peajes (€)</Label>
                  <Input
                    type="number"
                    value={completeForm.tolls}
                    onChange={(e) => setCompleteForm({ ...completeForm, tolls: e.target.value })}
                    placeholder={t('placeholder_amount')}
                  />
                </div>
                <div>
                  <Label>Aparcamiento (€)</Label>
                  <Input
                    type="number"
                    value={completeForm.parking}
                    onChange={(e) => setCompleteForm({ ...completeForm, parking: e.target.value })}
                    placeholder={t('placeholder_amount')}
                  />
                </div>
                <div>
                  <Label>Taxi/Transporte (€)</Label>
                  <Input
                    type="number"
                    value={completeForm.taxi}
                    onChange={(e) => setCompleteForm({ ...completeForm, taxi: e.target.value })}
                    placeholder={t('placeholder_amount')}
                  />
                </div>
                <div>
                  <Label>Otros (€)</Label>
                  <Input
                    type="number"
                    value={completeForm.other}
                    onChange={(e) => setCompleteForm({ ...completeForm, other: e.target.value })}
                    placeholder={t('placeholder_amount')}
                  />
                </div>
                <div>
                  <Label>Descripción Otros</Label>
                  <Input
                    value={completeForm.other_description}
                    onChange={(e) => setCompleteForm({ ...completeForm, other_description: e.target.value })}
                    placeholder={t('ph_description')}
                  />
                </div>
              </div>
            </div>

            {/* Photos */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Fotos
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fotos Recogida ({pickupPhotos.length})</Label>
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleUploadPhoto(e, 'pickup')}
                    disabled={uploadingPhoto}
                  />
                </div>
                <div>
                  <Label>Fotos Entrega ({deliveryPhotos.length})</Label>
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleUploadPhoto(e, 'delivery')}
                    disabled={uploadingPhoto}
                  />
                </div>
                <div>
                  <Label>Tickets Gastos ({expensePhotos.length})</Label>
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleUploadPhoto(e, 'expense')}
                    disabled={uploadingPhoto}
                  />
                </div>
                <div>
                  <Label>Contrato Firmado ({contractPhotos.length})</Label>
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleUploadPhoto(e, 'contract')}
                    disabled={uploadingPhoto}
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label>Notas de Finalización</Label>
              <Textarea
                value={completeForm.completion_notes}
                onChange={(e) => setCompleteForm({ ...completeForm, completion_notes: e.target.value })}
                placeholder={t('ph_observations_delivery')}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompleteDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCompleteService} 
              className="bg-green-600 hover:bg-green-700"
              disabled={uploadingPhoto}
            >
              <Check className="w-4 h-4 mr-2" />
              Completar Servicio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar Servicio</DialogTitle>
            <DialogDescription>
              Selecciona el tipo de cancelación
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => handleCancelOrder('normal')}
            >
              <X className="w-4 h-4 mr-2" />
              Cancelación Normal
            </Button>
            {(order.status === 'assigned' || order.status === 'in_transit') && (
              <>
                <Button
                  variant="outline"
                  className="w-full justify-start border-orange-300"
                  onClick={() => handleCancelOrder('pickup')}
                >
                  🚫 Cancelar en Salida (vehículo no disponible)
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start border-red-300"
                  onClick={() => handleCancelOrder('transit')}
                >
                  🛑 Cancelar en Transporte
                </Button>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photos Dialog */}
      <Dialog open={showPhotosDialog} onOpenChange={setShowPhotosDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Fotos del Servicio</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {loadingPhotos ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#36e961] mx-auto"></div>
                <p className="mt-2 text-gray-500">Cargando fotos...</p>
              </div>
            ) : vehiclePhotos && (vehiclePhotos.pickup_photos?.length > 0 || vehiclePhotos.delivery_photos?.length > 0) ? (
              <div className="space-y-6">
                {vehiclePhotos.pickup_photos?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2 text-green-700">Fotos de Recogida</h4>
                    <div className="grid grid-cols-3 gap-2">
                      {vehiclePhotos.pickup_photos.map((photo, idx) => (
                        <img
                          key={idx}
                          src={photo.startsWith('data:') ? photo : `${BACKEND_URL}${photo}`}
                          alt={`Recogida ${idx + 1}`}
                          className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-80"
                          onClick={() => window.open(photo.startsWith('data:') ? photo : `${BACKEND_URL}${photo}`, '_blank')}
                        />
                      ))}
                    </div>
                  </div>
                )}
                {vehiclePhotos.delivery_photos?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2 text-red-700">Fotos de Entrega</h4>
                    <div className="grid grid-cols-3 gap-2">
                      {vehiclePhotos.delivery_photos.map((photo, idx) => (
                        <img
                          key={idx}
                          src={photo.startsWith('data:') ? photo : `${BACKEND_URL}${photo}`}
                          alt={`Entrega ${idx + 1}`}
                          className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-80"
                          onClick={() => window.open(photo.startsWith('data:') ? photo : `${BACKEND_URL}${photo}`, '_blank')}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">No hay fotos disponibles</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPhotosDialog(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceDetailPage;
